// Generic library panel — slides in from the right on desktop, bottom
// sheet on mobile. Two-pane: list on the left, detail/editor on the
// right. Used as the visual shell for Skills / Memory / Prompt-Presets
// (the chat-actions drawer's three "open a surface" rows).
//
// The panel is dumb — it takes a render function for the list rows and
// for the detail pane. Each consumer owns its own data fetch, edit
// state, and save/delete behavior.

import { useEffect } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { Icon, type IconName } from "../../icons";

export interface LibraryPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon: IconName;
  /** Left-pane: list of items. Caller renders rows (selectable). */
  list: ComponentChildren;
  /** Right-pane: detail / edit form. Empty-state when nothing selected. */
  detail: ComponentChildren;
  /** Optional toolbar buttons rendered next to the close button. */
  toolbar?: ComponentChildren;
  /** Subtitle line under the title — small italic serif. */
  sub?: string;
}

export function LibraryPanel({ open, onClose, title, icon, list, detail, toolbar, sub }: LibraryPanelProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="lib-bg" onClick={onClose}>
      <aside className="lib-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <header className="lib-head">
          <span className="lib-head-icon"><Icon name={icon} size={14} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="lib-head-title">{title}</div>
            {sub && <div className="lib-head-sub">{sub}</div>}
          </div>
          {toolbar}
          <button className="icon-btn" onClick={onClose} title="Close"><Icon name="x" size={14} /></button>
        </header>

        <div className="lib-body">
          <div className="lib-list">{list}</div>
          <div className="lib-detail">{detail}</div>
        </div>
      </aside>
    </div>
  );
}

export function LibraryRow({
  active, name, description, badge, onClick,
}: {
  active: boolean;
  name: string;
  description?: string;
  badge?: string;
  onClick: () => void;
}) {
  return (
    <button className="lib-row" data-active={active} onClick={onClick} type="button">
      <div className="lib-row-name">
        <span>{name}</span>
        {badge && <span className="lib-row-badge">{badge}</span>}
      </div>
      {description && <div className="lib-row-desc">{description}</div>}
    </button>
  );
}

export function LibraryEmpty({ icon, headline, sub }: { icon: IconName; headline: string; sub: string }) {
  return (
    <div className="lib-empty">
      <span className="lib-empty-icon"><Icon name={icon} size={24} /></span>
      <h3 className="h-display" style={{ fontSize: 18, margin: "12px 0 4px" }}>{headline}</h3>
      <p className="h-italic" style={{ color: "var(--text-mute)", margin: 0, fontSize: 14 }}>{sub}</p>
    </div>
  );
}
